import type {
  CombinedInstrumentListItem,
  CombinedInstrumentUsage,
  Translated,
} from "@repo/shared/types";
import { useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { apiErrorBody, apiErrorCode } from "@/lib/api/_client";
import * as combinedInstrumentsApi from "@/lib/api/combined-instruments";
import {
  useCombinedInstruments,
  useDeleteCombinedInstrument,
} from "@/lib/hooks/use-combined-instruments";
import { useT } from "@/lib/i18n/use-t";
import { CombinedInstrumentChartDialog } from "./combined-instrument-chart-dialog";
import { CombinedInstrumentDialog } from "./combined-instrument-dialog";
import { CombinedInstrumentsLoader } from "./combined-instruments-loader";
import { CombinedInstrumentsTable } from "./combined-instruments-table";
import { T } from "./combined-instruments-wrapper.i18n";

const PAGE_SIZE = 20;

/** The refusal, spelled out: every kind of reference, named. */
function inUseMessage(usage: CombinedInstrumentUsage, t: Translated<typeof T>): string {
  const parts = [t.inUse];
  if (usage.products.length > 0) parts.push(t.usedByProducts + usage.products.join(", "));
  if (usage.assetClasses.length > 0) {
    parts.push(t.usedByAssetClasses + usage.assetClasses.join(", "));
  }
  if (usage.combinedInstruments.length > 0) {
    parts.push(t.usedByCombined + usage.combinedInstruments.join(", "));
  }
  return parts.join("\n");
}

/**
 * The 409 the delete answers when something started referencing the basket
 * between the pre-check below and the delete itself, or when the basket was
 * deleted from somewhere the pre-check never ran.
 */
function usageFromError(err: unknown): CombinedInstrumentUsage | null {
  if (apiErrorCode(err) !== "instrument_in_use") return null;
  const usage = apiErrorBody(err)?.usage as CombinedInstrumentUsage | undefined;
  return usage ?? null;
}

export function CombinedInstrumentsWrapper() {
  const t = useT(T);

  const [currentPage, setCurrentPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CombinedInstrumentListItem | null>(null);
  const [chartDialogOpen, setChartDialogOpen] = useState(false);
  const [chartId, setChartId] = useState<string | null>(null);
  const [chartName, setChartName] = useState<string | null>(null);
  const [chartIsin, setChartIsin] = useState<string | null>(null);
  const [alertConfig, setAlertConfig] = useState<{
    open: boolean;
    title: string;
    description: string;
    variant: "default" | "destructive";
  }>({ open: false, title: "", description: "", variant: "default" });

  const { data, isLoading } = useCombinedInstruments(currentPage, PAGE_SIZE);
  const deleteMutation = useDeleteCombinedInstrument();

  const instruments = data?.combined ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const handleCreate = () => {
    setEditingId(null);
    setDialogOpen(true);
  };

  const handleEdit = (item: CombinedInstrumentListItem) => {
    setEditingId(item.id);
    setDialogOpen(true);
  };

  // Asked before the confirmation dialog opens, so a basket that cannot be
  // deleted is never confirmed — the service refuses it either way (409).
  const handleDelete = async (item: CombinedInstrumentListItem) => {
    try {
      const usage = await combinedInstrumentsApi.getUsage(item.id);
      if (!usage.canDelete) {
        setAlertConfig({
          open: true,
          title: t.deleteFailed,
          description: inUseMessage(usage, t),
          variant: "destructive",
        });
        return;
      }
      setDeleteTarget(item);
    } catch {
      setAlertConfig({
        open: true,
        title: t.deleteFailed,
        description: "",
        variant: "destructive",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setAlertConfig({
        open: true,
        title: t.deleteSuccess,
        description: "",
        variant: "default",
      });
    } catch (err) {
      const usage = usageFromError(err);
      setAlertConfig({
        open: true,
        title: t.deleteFailed,
        description: usage ? inUseMessage(usage, t) : "",
        variant: "destructive",
      });
    }
    setDeleteTarget(null);
  };

  const handleNameClick = (item: CombinedInstrumentListItem) => {
    setChartId(item.id);
    setChartName(item.name);
    setChartIsin(item.isin);
    setChartDialogOpen(true);
  };

  const handleChartDialogClose = () => {
    setChartDialogOpen(false);
    setChartId(null);
    setChartName(null);
    setChartIsin(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={handleCreate}>{t.addCombinedInstrument}</Button>
      </div>

      {isLoading && instruments.length === 0 ? (
        <CombinedInstrumentsLoader />
      ) : (
        <CombinedInstrumentsTable
          items={instruments}
          currentPage={currentPage}
          totalCount={totalCount}
          totalPages={totalPages}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onNameClick={handleNameClick}
          isLoading={isLoading || deleteMutation.isPending}
        />
      )}

      <CombinedInstrumentDialog
        open={dialogOpen}
        editingId={editingId}
        onClose={handleDialogClose}
      />

      <CombinedInstrumentChartDialog
        open={chartDialogOpen}
        id={chartId}
        name={chartName}
        isin={chartIsin}
        onClose={handleChartDialogClose}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t.deleteTitle}
        description={t.deleteDescription(deleteTarget?.name ?? "")}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDeleteConfirm}
        variant="destructive"
      />

      <AlertMessage
        open={alertConfig.open}
        onOpenChange={(open) => setAlertConfig((prev) => ({ ...prev, open }))}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
